import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, Mail, Lock, ArrowRight, Shield, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BetaLogin = ({ setUser }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/beta/login`, 
        { email, password },
        { withCredentials: true }
      );
      
      const userData = response.data;
      setUser(userData);
      
      // Check if password reset is required
      if (userData.must_reset_password) {
        navigate("/reset-password", { state: { fromLogin: true } });
      } else {
        toast.success("Welcome back!", {
          description: "Starting your ClarityOS session..."
        });
        navigate("/progress");
      }
    } catch (err) {
      let message;
      if (err.response?.data?.detail) {
        message = err.response.data.detail;
      } else if (err.response?.status) {
        message = `Server error (${err.response.status}). Please try again later.`;
      } else if (err.code === 'ERR_NETWORK') {
        message = "Cannot reach the server. Please check your internet connection.";
      } else {
        message = "Login failed. Please try again.";
      }
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    
    setForgotLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success("Reset link sent!", {
        description: "Check your email for password reset instructions."
      });
      setShowForgotPassword(false);
      setForgotEmail("");
    } catch (err) {
      toast.error("Failed to send reset email");
    } finally {
      setForgotLoading(false);
    }
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-clarity-bg relative overflow-hidden flex items-center justify-center p-6">
        <div className="absolute inset-0 grid-pattern opacity-50"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-brand-tertiary/10 rounded-full blur-3xl"></div>

        <div className="relative z-10 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_clarity-wellness-log/artifacts/dv3nbk4x_Logo%20new%20copy.jpg" 
                alt="ClarityOS" 
                className="w-12 h-12 rounded-xl object-cover"
              />
              <span className="text-3xl tracking-tight" style={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
                <span className="font-bold text-white">Clarity</span>
                <span className="font-normal text-brand-primary">OS</span>
              </span>
            </div>
          </div>

          <div className="glass rounded-2xl p-8">
            <h1 className="font-heading text-2xl font-bold text-white text-center mb-2">
              Forgot Password
            </h1>
            <p className="text-slate-400 text-center mb-8">
              Enter your email and we'll send you a reset link
            </p>

            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-slate-400">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="pl-10 bg-clarity-subtle/50 border-white/10 text-white placeholder:text-slate-600 focus:border-brand-primary"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={forgotLoading}
                className="w-full bg-brand-primary text-clarity-bg font-bold py-3 rounded-lg hover:bg-yellow-400 hover:shadow-[0_0_20px_rgba(229,184,76,0.4)] transition-all disabled:opacity-50"
                data-testid="send-reset-link-btn"
              >
                {forgotLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-clarity-bg border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </span>
                ) : (
                  "Send Reset Link"
                )}
              </Button>
            </form>

            <button
              onClick={() => setShowForgotPassword(false)}
              className="w-full mt-4 flex items-center justify-center gap-2 text-slate-400 hover:text-white transition-colors"
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-clarity-bg relative overflow-hidden flex items-center justify-center p-6">
      {/* Background pattern */}
      <div className="absolute inset-0 grid-pattern opacity-50"></div>
      
      {/* Ambient glow */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-brand-tertiary/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_clarity-wellness-log/artifacts/dv3nbk4x_Logo%20new%20copy.jpg" 
              alt="ClarityOS" 
              className="w-12 h-12 rounded-xl object-cover"
            />
            <span className="text-3xl tracking-tight" style={{ fontFamily: 'Montserrat, Arial, sans-serif' }}>
              <span className="font-bold text-white">Clarity</span>
              <span className="font-normal text-brand-primary">OS</span>
            </span>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-tertiary/10 border border-brand-tertiary/30">
            <Shield className="w-4 h-4 text-brand-tertiary" />
            <span className="text-sm text-brand-tertiary font-medium">Beta Access</span>
          </div>
        </div>

        {/* Login Card */}
        <div className="glass rounded-2xl p-8">
          <h1 className="font-heading text-2xl font-bold text-white text-center mb-2">
            Welcome Back
          </h1>
          <p className="text-slate-400 text-center mb-8">
            Sign in with your beta credentials
          </p>

          <form onSubmit={handleLogin} className="space-y-6">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-slate-400">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="pl-10 bg-clarity-subtle/50 border-white/10 text-white placeholder:text-slate-600 focus:border-brand-primary"
                  data-testid="beta-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-400">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pl-10 pr-10 bg-clarity-subtle/50 border-white/10 text-white placeholder:text-slate-600 focus:border-brand-primary"
                  data-testid="beta-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-primary text-clarity-bg font-bold py-3 rounded-lg hover:bg-yellow-400 hover:shadow-[0_0_20px_rgba(229,184,76,0.4)] transition-all disabled:opacity-50"
              data-testid="beta-login-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-clarity-bg border-t-transparent rounded-full animate-spin"></div>
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <p className="text-slate-500 text-sm text-center mt-6">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-brand-primary hover:underline"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </button>
          </p>
          
          <p className="text-slate-500 text-sm text-center mt-4">
            Don't have beta access?{" "}
            <button
              onClick={() => navigate("/request-access")}
              className="text-brand-primary hover:underline"
            >
              Request Access
            </button>
          </p>
        </div>

        {/* Footer */}
        <p className="text-slate-600 text-xs text-center mt-6">
          By signing in, you agree to the ClarityOS Beta Terms of Service
        </p>
      </div>
    </div>
  );
};
